import { styled, css } from 'styled-components';

import { Media } from '../../../theme/Media.tsx';
import {
  BORDER_RADIUS,
  CSS_VAR_SCROLL_LEFT,
  CSS_VAR_SCROLL_TOP,
  CSS_VAR_VISIBLE_HEIGHT,
  CSS_VAR_VISIBLE_WIDTH,
  DATA_ATTR_DRAGGING,
  DATA_ATTR_HAS_HORIZONTAL_SCROLLBAR,
  DATA_ATTR_HAS_VERTICAL_SCROLLBAR,
  DATA_ATTR_IS_EXPANDED,
  DATA_ATTR_TABLE_GROUP,
  DATA_ATTR_TABLE_HEADROW,
  DATA_ATTR_TABLE_NODATA,
  DATA_ATTR_TABLE_ROW,
  DATA_ATTR_TABLE_SCROLLABLE,
  DATA_ATTR_TABLE_SECTION,
  GROUP_SPACING,
  Z_INDEX_SCROLL_DIVIDER,
} from './constants.ts';

// Selector aliases for readability
const section = `[${DATA_ATTR_TABLE_SECTION}]`;
const nodata = `[${DATA_ATTR_TABLE_NODATA}]`;
const scrollable = `[${DATA_ATTR_TABLE_SCROLLABLE}]`;
// Every Row always carries the data-is-expanded attribute (true OR false), so this presence
// selector matches "the grid contains at least one row" — NOT "a row is expanded". Do not change
// it to [data-is-expanded='true']: that would hide a normal mobile table of collapsed rows.
const hasRows = `[${DATA_ATTR_IS_EXPANDED}]`;
const contentSection = `${section}:not(${nodata})`;
const group = `[${DATA_ATTR_TABLE_GROUP}]`;
const headrow = `[${DATA_ATTR_TABLE_HEADROW}]`;
const row = `[${DATA_ATTR_TABLE_ROW}]`;

export interface WrapperProps {
  $inner?: boolean;
}

export const StyledWrapper = styled.div<WrapperProps>`
  position: relative;
  width: 100%;

  @media ${Media.smMin} {
    border-radius: ${BORDER_RADIUS};
    overflow: hidden;

    & > :first-child {
      border-top-left-radius: ${BORDER_RADIUS};
      border-top-right-radius: ${BORDER_RADIUS};
    }

    & > :last-child {
      border-bottom-left-radius: ${BORDER_RADIUS};
      border-bottom-right-radius: ${BORDER_RADIUS};
    }

    ${({ theme, $inner }) =>
      $inner
        ? css`
            border: 1px solid ${theme.colors.grey10_1};
          `
        : css`
            ${theme.shadows.small}
          `}

    & > * + * {
      border-top: 1px solid ${({ theme }) => theme.colors.grey10_1};
    }

    ${({ $inner }) =>
      !$inner &&
      css`
        & > :not(:last-child):has(${group}:last-child) {
          padding-bottom: ${GROUP_SPACING};
        }
      `}
  }

  @media ${Media.xsMax} {
    & > ${section} {
      border-radius: ${BORDER_RADIUS};
      overflow: hidden;
    }

    & > ${scrollable} {
      border-radius: ${BORDER_RADIUS};
    }

    /* Adjacent content sections merge visually (no rounded corners between them) */
    & > ${contentSection} + ${contentSection} {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }

    & > ${contentSection}:has(+ ${contentSection}) {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    /* Spacing between different element types */
    & > ${section} + :not(${section}),
    & > :not(${section}) + ${section}, & > ${nodata} + ${section}, & > ${section} + ${nodata} {
      margin-top: ${GROUP_SPACING};
    }

    /* Hide the empty grid container on mobile (no data rows) so only the NoData card shows */
    & > ${scrollable}:not(:has(${hasRows})) {
      display: none;
    }

    & > ${scrollable}:first-child:not(:has(${hasRows})) + ${nodata} {
      margin-top: 0;
    }

    ${({ theme, $inner }) =>
      $inner
        ? css`
            & > ${section} {
              border: 1px solid ${theme.colors.grey10_1};
            }

            & > ${contentSection} + ${contentSection} {
              border-top: none;
            }
          `
        : css`
            & > ${section} {
              ${theme.shadows.small}
            }

            & > ${contentSection} + ${contentSection} {
              border-top: 1px solid ${theme.colors.grey10_1};
            }
          `}
  }
`;

export const ActionGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media ${Media.xsMax} {
    width: 100%;
    gap: ${GROUP_SPACING};
  }
`;

interface TableScrollableWrapperProps {
  $maxHeight?: string;
}

export const TableScrollableWrapper = styled.div<TableScrollableWrapperProps>`
  position: relative;
  overflow-x: auto;
  // overflow-x:auto silently forces overflow-y to compute to auto (CSS overflow quirk), making this a
  // vertical scroll container even with no max-height. During a drag, @dnd-kit translates rows toward/past
  // the bottom edge, and transformed content counts toward the scrollable region — surfacing a phantom
  // vertical scrollbar (most visible dragging a row to/below the last slot). Pin overflow-y explicitly:
  // scroll only when a max-height actually constrains the box, otherwise clip — the box already grows to fit
  // its content, so nothing real is clipped, only the transient drag overflow.
  overflow-y: ${({ $maxHeight }) => ($maxHeight ? 'auto' : 'hidden')};
  ${({ $maxHeight }) =>
    $maxHeight &&
    css`
      max-height: ${$maxHeight};
    `}

  /* While a row is being dragged, freeze horizontal scroll so the overlay stays aligned. */
  &[${DATA_ATTR_DRAGGING}='true'] {
    overflow-x: hidden;
  }
`;

interface StyledGridProps {
  $columns: string;
  $inner?: boolean;
  $fluid?: boolean;
}

export const StyledGrid = styled.div<StyledGridProps>`
  // With a fluid (shrink-to-zero) column, constrain the grid to the viewport so that track actually
  // shrinks and its content wraps. Otherwise size to content (min one viewport) so the table grows
  // and scrolls horizontally when its columns need more room than the viewport.
  ${({ $fluid }) =>
    $fluid
      ? css`
          width: 100%;
        `
      : css`
          width: max-content;
          min-width: 100%;
        `}
  display: grid;
  ${({ $columns }) =>
    $columns &&
    css`
      grid-template-columns: ${$columns};
    `}

  [${DATA_ATTR_HAS_VERTICAL_SCROLLBAR}='true'] &::before {
    content: '';
    position: absolute;
    left: calc(var(${CSS_VAR_SCROLL_LEFT}) + var(${CSS_VAR_VISIBLE_WIDTH}) - 1px);
    top: var(${CSS_VAR_SCROLL_TOP});
    height: var(${CSS_VAR_VISIBLE_HEIGHT});
    width: 1px;
    background: ${({ theme }) => theme.colors.grey10_1};
    z-index: ${Z_INDEX_SCROLL_DIVIDER};
    pointer-events: none;
  }

  [${DATA_ATTR_HAS_HORIZONTAL_SCROLLBAR}='true'] &::after {
    content: '';
    grid-column: 1 / -1;
    position: sticky;
    bottom: 0;
    height: 1px;
    background: ${({ theme }) => theme.colors.grey10_1};
    z-index: ${Z_INDEX_SCROLL_DIVIDER};
    pointer-events: none;
  }

  @media ${Media.smMin} {
    // Each row owns both its top and bottom border. Adjacent rows overlap their
    // 1px borders via negative margin so a single visible line appears at every
    // boundary. When a row is visibility-hidden during drag, its own borders are
    // suppressed but the neighbors' borders still paint at the slot edges — the
    // gap stays symmetric without a sibling-conditioned compensation rule, and
    // the lib's translate animation can move rows around without separators
    // appearing or disappearing.
    & > * {
      border-top: 1px solid ${({ theme }) => theme.colors.grey10_1};
      border-bottom: 1px solid ${({ theme }) => theme.colors.grey10_1};
    }

    & > * + * {
      margin-top: -1px;
    }

    // Groups are containers — their inner rows own the visible borders. Without
    // this override GroupWrapper itself would paint a straight border-bottom
    // under the last row's rounded corners.
    & > ${group} {
      border-top: none;
      border-bottom: none;
    }

    & > ${headrow} + * {
      border-top: none;
    }

    & > ${group} + ${group}, & > :not(${group}) + ${group} {
      margin-top: ${GROUP_SPACING};
      border-top: none;
    }

    & > ${group} + :not(${group}) {
      margin-top: ${GROUP_SPACING};
    }

    & > :first-child {
      border-top: none;
    }

    & > :last-child {
      border-bottom: none;
    }

    ${({ theme, $inner }) =>
      $inner &&
      css`
        & > ${group} + :not(${group}) {
          margin-top: 0;
        }

        & > ${row}:has(+ ${group}) {
          border-bottom: 1px solid ${theme.colors.grey10_1};
        }

        & > ${group}:not(:has(+ ${group})) ${row}:last-child {
          border-bottom: none;
        }
      `}
  }

  @media ${Media.xsMax} {
    width: 100%;
    display: flex;
    flex-direction: column;

    & > ${headrow} + ${group} {
      margin-top: 0;
    }
  }
`;
